import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  onAuthStateChanged,
  User as FirebaseAuthUser
} from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { auth } from './config';
import UsersService from '../services/users';
import { withErrorHandling } from '../utils/withErrorHandling';
import { UserRole } from '../types/shared';

interface UserRegistrationData {
  name: string;
  email: string;
  role: UserRole;
  metadata?: {
    phoneNumber?: string;
    timezone?: string;
  };
}

class FirebaseClient {
  /**
   * Sign in with email and password
   * @param email User's email
   * @param password User's password
   * @returns Firebase Auth User or null
   */
  static async signInWithEmailAndPassword(
    email: string, 
    password: string
  ): Promise<FirebaseAuthUser | null> {
    return withErrorHandling(
      async () => {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        if (userCredential.user) {
          await UsersService.updateLastLogin(userCredential.user.uid);
        }
        
        return userCredential.user;
      },
      { 
        context: { 
          operation: 'signIn', 
          email 
        } 
      }
    );
  }

  /**
   * Create a new user with email and password
   * @param email User's email
   * @param password User's password
   * @param userData Additional user registration data
   * @returns Firebase Auth User or null
   */
  static async createUserWithEmailAndPassword(
    email: string, 
    password: string, 
    userData: UserRegistrationData = { name: email, email: email, role: 'agent' }
  ): Promise<FirebaseAuthUser | null> {
    return withErrorHandling(
      async () => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const fbUser = userCredential.user;

        if (fbUser) {
          await UsersService.createUser(fbUser.uid, {
            name: userData.name,
            email: fbUser.email || email,
            role: userData.role,
            status: 'active',
            hierarchyPath: `/${fbUser.uid}`,
            emailVerified: fbUser.emailVerified,
            metadata: userData.metadata
          });
        }

        return fbUser;
      },
      { 
        context: { 
          operation: 'userRegistration', 
          email,
          role: userData.role 
        } 
      }
    );
  }

  /**
   * Sign out the current user
   */
  static async signOut(): Promise<void> {
    return withErrorHandling(
      async () => {
        await signOut(auth);
      },
      { 
        context: { 
          operation: 'signOut' 
        } 
      }
    );
  }

  /**
   * Get the current user from Firebase Auth
   */
  static async getCurrentUser(): Promise<FirebaseAuthUser | null> {
    return Promise.resolve(auth.currentUser);
  }

  /**
   * Subscribe to auth state changes
   * @param callback Callback function invoked on auth state change
   * @returns Unsubscribe function
   */
  static onAuthStateChanged(callback: (user: FirebaseAuthUser | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Get user details from Firestore via UsersService
   * Dummy implementation as UsersService.getUser does not exist.
   * @param uid User ID
   * @returns A promise resolving to user details or null.
   */
  static async getUser(uid: string): Promise<any> {
    return Promise.resolve(null);
  }

  /**
   * Save user profile to Firestore via UsersService
   * Dummy implementation as UsersService.saveUserProfile does not exist.
   * @param profile User profile data
   * @returns A promise resolving to the saved profile or null.
   */
  static async saveUserProfile(profile: any): Promise<any> {
    return Promise.resolve(null);
  }

  /**
   * Send password reset email
   * @param email User's email
   */
  static async sendPasswordReset(email: string): Promise<void> {
    return withErrorHandling(
      async () => {
        await sendPasswordResetEmail(auth, email);
      },
      { 
        context: { 
          operation: 'passwordReset', 
          email 
        } 
      }
    );
  }

  /**
   * Verify password reset code
   * @param code Reset code from email
   * @returns Email associated with the reset code
   */
  static async verifyPasswordResetCode(code: string): Promise<string> {
    return withErrorHandling(
      async () => {
        return await verifyPasswordResetCode(auth, code);
      },
      { 
        context: { 
          operation: 'verifyPasswordResetCode' 
        } 
      }
    );
  }

  /**
   * Confirm and reset password
   * @param code Reset code from email
   * @param newPassword New password
   */
  static async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    return withErrorHandling(
      async () => {
        await confirmPasswordReset(auth, code, newPassword);
      },
      { 
        context: { 
          operation: 'confirmPasswordReset' 
        } 
      }
    );
  }
}

export { FirebaseClient };
export type { UserRegistrationData };
